import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Clock, Building2, Factory, ArrowRight } from 'lucide-react';
import { InquiryForm } from '@/components/lead/InquiryForm';
import { cn } from '@/lib/utils';

// --- Corporate Contact Data ---
const LOCATIONS = [
  {
    type: 'showroom',
    title: 'Genel Merkez & Showroom',
    address: 'Gürdinç sk 24/A Defterdar Mah, Eyüp, İstanbul, Turkey',
    phone: '+90 553 794 21 61',
    email: 'info@turcosandalye.com',
    hours: 'Pzt - Cmt: 09:00 - 19:00',
    mapLink: 'https://maps.google.com/?q=Defterdar+Mah+Eyup+Istanbul'
  },
  {
    type: 'factory',
    title: 'Fabrika',
    address: 'Güneşli Mah. 1277 Sok. No:3 Bağcılar/İstanbul',
    phone: '+90 553 794 21 61',
    email: 'info@turcosandalye.com',
    hours: 'Pzt - Cmt: 09:00 - 19:00',
    mapLink: 'https://maps.google.com/?q=Güneşli+Mah+1277+Sok+No:3+Bağcılar+İstanbul'
  }
];

const DEPARTMENTS = [
  {
    name: 'Nurullah Çolak',
    phone: '0553 794 21 61'
  }
];

export function ContactPage() {
  return (
    <>
      <title>İletişim | Turco Sandalye - Profesyonel Çözüm Merkezi</title>

      {/* Hero Section */}
      <section className="bg-charcoal text-white pt-40 pb-20 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-white/5 skew-x-12 -translate-x-20" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 text-gold mb-4">
              <span className="h-[1px] w-12 bg-gold" />
              <span className="text-xs font-bold uppercase tracking-widest">İletişim & Destek</span>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl mb-6">
              Projeleriniz İçin <br />
              <span className="text-gray-400">Profesyonel Çözüm Ortaklığı</span>
            </h1>
            <p className="text-xl text-gray-400 font-light max-w-2xl leading-relaxed">
              Mimar, tasarımcı ve işletmeciler için özel üretim mobilya çözümleri sunuyoruz.
              Detaylı bilgi ve fiyat teklifi için uzman ekibimizle iletişime geçin.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="bg-white min-h-screen">
        <div className="container mx-auto px-6 lg:px-12 -mt-10 relative z-20 pb-24">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">

            {/* LEFT: Contact Information */}
            <div className="lg:col-span-5 space-y-12 pt-10">

              {/* Locations */}
              <div className="space-y-8">
                {LOCATIONS.map((loc, idx) => (
                  <motion.div
                    key={loc.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-stone-50 p-8 border border-gray-100 group hover:border-charcoal transition-colors duration-300"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className={cn("w-10 h-10 flex items-center justify-center text-white", loc.type === 'showroom' ? 'bg-charcoal' : 'bg-charcoal/80')}>
                          {loc.type === 'showroom' ? <Building2 size={20} /> : <Factory size={20} />}
                        </div>
                        <h3 className="font-display text-xl text-charcoal">{loc.title}</h3>
                      </div>
                    </div>

                    <div className="space-y-4 text-sm text-gray-600">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-charcoal shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{loc.address}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-charcoal shrink-0" />
                        <a href={`tel:${loc.phone}`} className="hover:text-gold transition-colors font-medium">{loc.phone}</a>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-charcoal shrink-0" />
                        <a href={`mailto:${loc.email}`} className="hover:text-gold transition-colors">{loc.email}</a>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-charcoal shrink-0" />
                        <span>{loc.hours}</span>
                      </div>
                    </div>

                    <a
                      href={loc.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-charcoal hover:text-gold transition-colors border-b border-gray-200 hover:border-gold pb-1"
                    >
                      Haritada Görüntüle
                      <ArrowRight size={14} />
                    </a>
                  </motion.div>
                ))}
              </div>

              {/* Specific Departments */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="pt-8 border-t border-gray-100"
              >
                <h3 className="text-sm font-bold uppercase tracking-widest text-charcoal mb-6">Departman İletişim</h3>
                <div className="grid gap-4">
                  {DEPARTMENTS.map((dept) => (
                    <div key={dept.name} className="flex items-center justify-between p-4 bg-white border border-gray-100 hover:shadow-md transition-all">
                      <span className="font-medium text-charcoal">{dept.name}</span>
                      <a href={`tel:${dept.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-charcoal font-medium">
                        <Phone size={14} />
                        {dept.phone}
                      </a>
                    </div>
                  ))}
                </div>
              </motion.div>

            </div>

            {/* RIGHT: Inquiry Form */}
            <div className="lg:col-span-7">
              <div className="bg-white shadow-2xl shadow-charcoal/5 border border-gray-100 p-8 lg:p-12 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-charcoal via-gray-600 to-gray-400" />

                <div className="mb-8">
                  <h2 className="font-display text-3xl text-charcoal mb-2">Bize Ulaşın</h2>
                  <p className="text-gray-500">
                    Aşağıdaki formu doldurarak projeniz hakkında bilgi verebilir veya genel sorularınızı iletebilirsiniz.
                    Müşteri temsilcilerimiz en kısa sürede size dönüş yapacaktır.
                  </p>
                </div>

                <InquiryForm sourcePage="contact_page" className="p-0 shadow-none border-0" />
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
